import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Footer from '../_core/Footer';
import Seo from 'components/Seo';


import { getEnvVar } from 'utils/get-env-var';
import { paramsFromQuerystring } from 'utils/params-from-querystring';
import { tripleWhale, tripleWhaleCustom, tripleWhaleLineItems } from 'utils/triple-whale';
import { pushConversion } from 'utils/push-conversion';
import axios from 'axios';

import LogoCircle from 'components/_core/LogoCircle';
import YouTubeVideoThumb from 'components/Home/VideoThumb';
import ThumbPhoto from 'img/thank-you-thumb.jpg';
import { ButtonLink } from 'components/_core/Button';
import { Testimonial } from 'components/Home/TestimonialGrid';

const stackPoint = 700;

// the checkout session whose conversion events have already been reported
const PROCESSED_KEY = 'tw_processed_checkout';

// Triple Whale custom event fired when a free trial starts. A matching Custom
// Event must exist in the Triple Whale app under exactly this name and be marked
// a conversion event, or the trials get no attribution.
const TRIAL_EVENT = 'TrialStarted';

const Container = styled.div`
    background-color: #f0fbfd;
    padding: 100px 15px;


    @media (max-width: ${stackPoint}px) {
        padding: 40px 15px;
    }
`;

const Wrapper = styled.div`
    max-width: 1000px;
    margin: 0 auto;
`;

const Top = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media (max-width: ${stackPoint}px) {
        display: block;
    }
`;

const Left = styled.div`
    flex-basis: 50%;
    margin-right: 30px;

    h1, h2 {
        margin: 0;
    }

    h1 {
        font-size: 40px;
        margin-top: 5px;
    }

    h2 {
        // font-weight: normal;
        font-size: 18px;
    }

    p {
        line-height: 1.4em;
    }

    @media (max-width: ${stackPoint}px) {
        margin-right: 0;
        max-width: 500px;

        h1 {
            margin-top: 15px;
        }

        h2 {
            margin-bottom: 10px;
        }
    }
`;
const Right = styled.div`
    flex-basis: 50%;
    
    @media (max-width: ${stackPoint}px) {
        display: none;
    }
    
`;

const GettingStarted = styled.div`
    @media (min-width: ${stackPoint}px) {
        margin-top: -22px;
    }
`;

const StepWrap = styled.div`
    border-radius: 8px;
    background-color: #fff;
    padding: 10px 0;
    margin: 0;
    margin-top: 20px;  
    border: 1px solid #666;

    @media (max-width: ${stackPoint}px) {
        padding: 0;
    }
`;

const Steps = styled.ol`
    margin: 0;
    padding: 0;

    display: flex;
    align-items: center;

    list-style: none;
    counter-reset: item;

    @media (max-width: ${stackPoint}px) {
        display: block;
        padding-top: 10px;
    }
`;

const Step = styled.li`
    flex-basis: 33%;
    padding: 0 20px 0 0;

    &:first-child {
        padding-left: 30px;
        flex-basis: 31%;
    }

    &:nth-child(2) {
        flex-basis: 31%;
    }

    &:last-child {
        flex-basis: 38%;
    }

    @media (max-width: ${stackPoint}px) {
        padding: 0 15px 15px 15px !important;

        &:last-child {
            padding-bottom: 0;
        }
    }

    margin: 0;
    display: flex;
    align-items: center;
    font-size: 14px;

    &::before {
        display: block;
        content: counter(item);
        counter-increment: item;

        color: ${props => props.theme.colors.teal};
        font-weight: 900;
        font-size: 75px;
        margin-right: 15px;
    }
`;

const MobileVideo = styled.div`
    display: none;

    @media (max-width: ${stackPoint}px) {
        display: block;
        margin: 30px auto;
    }
`;

const Bottom = styled.div`
    margin: 0 auto 0;
    text-align: center;

    ${Testimonial} {
        margin: 0 auto;
        max-width: 400px;

        p {
            font-size: 14px !important;
        }
    }
`;

const CTA = styled(ButtonLink)`
    text-transform: none;
    font-weight: bold;
    padding: 13px 50px;
    border: 2px solid #fff;
    box-shadow: 0px 6px 0 0 #00000021;

    margin: 20px 0;
`;

const ThankYou = () => {
    // fallback identifier for the pixel events: the stash below is written before
    // the Stripe redirect and can be missing an email if the account had not
    // loaded yet at that point
    const userEmail = useSelector(state => state.user?.userData?.email);
    const userEmailRef = useRef(userEmail);
    userEmailRef.current = userEmail;

    useEffect(() => {
        const params = paramsFromQuerystring();

        if (params.checkout_session) {
            const orderId = params.checkout_session;

            // Refreshing this page re-runs the effect with the session id still in
            // the URL — the query param is only stripped once the request below
            // resolves. The server-side conversions dedupe on a deterministic event
            // id, but the Triple Whale pixel events have no such protection, so
            // remember which checkout has already been reported.
            //
            // Safe to skip the request on a repeat: the order is also submitted by
            // the checkout.session.completed webhook.
            let processed = null;
            try {
                processed = localStorage.getItem(PROCESSED_KEY);
            } catch (e) {}

            if (processed === orderId) {
                window.history.pushState({}, document.title, window.location.pathname);
                return;
            }

            try {
                localStorage.setItem(PROCESSED_KEY, orderId);
            } catch (e) {}

            // read the checkout info stashed before the Stripe redirect (redux
            // state does not survive the round-trip back to this page)
            let checkout = {};
            try {
                checkout = JSON.parse(localStorage.getItem('tw_checkout') || '{}');
                localStorage.removeItem('tw_checkout');
            } catch (e) {}

            const email = checkout.email || userEmailRef.current;

            // Purchase pixel event for attribution, fired for trials too so the
            // session is captured while the browser is still here.
            //
            // For a paid checkout the authoritative order + revenue record is
            // submitted server-side from /subscribe-after-success and the
            // checkout.session.completed webhook, and orderId here matches the
            // order_id used by both, so the three stitch together. A trial
            // collects $0 and so gets no order record until it converts — see the
            // TRIAL_EVENT custom event below, which is what carries the trial
            // through attribution.
            let purchaseFired = false;
            let plan = checkout.plan;

            const firePurchase = () => {
                if (purchaseFired) {
                    return;
                }

                purchaseFired = true;
                tripleWhale('Purchase', {
                    email: email || userEmailRef.current,
                    orderId,
                    lineItems: tripleWhaleLineItems(plan)
                });
            };

            // lineItems is required on Purchase, and the plan it names comes from a
            // localStorage stash written before the Stripe redirect — which is
            // empty whenever storage is blocked or cleared (Safari private mode,
            // ITP). With the plan in hand fire straight away, which is the order
            // Triple Whale recommends: pixel event first, order record second.
            // Without it, wait for the server, which knows the plan from the
            // session, rather than reporting a purchase of nothing.
            if (plan) {
                firePurchase();
            }

            axios.post(`${getEnvVar('API_BASE')}/subscribe-after-success`, { id: orderId })
                .then(resp => {
                    // hand the server's Meta event name + id to GTM so the browser
                    // tag sends the same pair and Meta collapses the two into one
                    pushConversion(resp?.data?.conversion);

                    plan = plan || resp?.data?.tripleWhale?.plan;
                    firePurchase();

                    const subscriptionId = resp?.data?.tripleWhale?.subscriptionId;

                    if (resp?.data?.tripleWhale?.isTrial) {
                        // A trial is not a subscription yet: the server holds the
                        // subscription record back until the trial converts to a
                        // paid cycle, so NewSubscription here would describe a
                        // subscription Triple Whale has never been sent. The trial
                        // start is tracked as a custom event instead, which is
                        // marked a conversion event in the Triple Whale app so it
                        // runs through attribution on its own.
                        tripleWhaleCustom(TRIAL_EVENT, {
                            email: email || userEmailRef.current,
                            orderId,
                            subscriptionId,
                            plan
                        });
                    } else if (subscriptionId) {
                        // fire NewSubscription once we have the subscription id back
                        tripleWhale('NewSubscription', {
                            email: email || userEmailRef.current,
                            orderId,
                            subscriptionId,
                            lineItems: tripleWhaleLineItems(plan)
                        });
                    }
                })
                .finally(() => {
                    // the request failing must not cost us the Purchase event, even
                    // though without the server's plan it goes without lineItems
                    firePurchase();
                    window.history.pushState({}, document.title, window.location.pathname);
                });
        } else {
            window.location.href = '/';
        }

    }, []);
 
    return (
        <>
            {/* post-purchase page — noindex so checkout confirmations never surface in search */}
            <Seo title="Welcome to the JP Bouvet Method" noindex />
            <Container>
                <Wrapper>
                    <Top>
                        <Left>
                            <LogoCircle size="small" />
                            <h1>Congratulations!</h1>
                            <h2>Your journey to improvisational freedom starts now.</h2>
                            <p>Thank you for trusting me with your drumming education. Let's get you oriented on the website and help you find the right courses to help you <strong><em>FIND YOUR FREEDOM</em></strong>.</p>
                        </Left>
                        <Right>
                            <YouTubeVideoThumb videoId="ygskxToYB40" thumb={ThumbPhoto} smallPlayBtn rounded playBtnText="Play Welcome Video" />
                        </Right>
                    </Top>
                    <GettingStarted>
                        <h2>Getting Started:</h2>
                        <StepWrap>
                            <Steps>
                                <Step>
                                    Watch the 90 second "Welcome Video" on this page.
                                </Step>
                                <Step>
                                    Check your email for the "Website Orientation video."
                                </Step>
                                <Step>
                                    Within 24 hours, you'll get an email from your Method Mentor. This is your personal coach. They'll help you decide which course(s) to start with.
                                </Step>
                            </Steps>
                        </StepWrap>
                    </GettingStarted>
                    
                    <MobileVideo>
                        <YouTubeVideoThumb videoId="ygskxToYB40" thumb={ThumbPhoto} smallPlayBtn rounded playBtnText="Play Welcome Video" />
                    </MobileVideo>

                    <Bottom>
                        <CTA to="/" pill large orange>Enter Website</CTA>
                        <Testimonial>
                            <p>“You and your site have changed my drumming life more than you could imagine. Thank you.”</p>
                            <p className="gray">&mdash; Nathan B.</p>
                        </Testimonial>
                    </Bottom>
                </Wrapper>
            </Container>
            <Footer />
        </>
    );
};

export default ThankYou;
